import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import styled, { css } from 'styled-components';
import { useScrollPosition } from '../../lib/hooks';

const DefaultFab = styled.button`
    min-width: 40px;
    height: 40px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.16px;
    line-height: 20px;
    text-transform: uppercase;
    border: none;
    outline: none;
    cursor: pointer;
    padding: 0 12px;
    box-shadow: ${(props) => props.theme.shadows['shadow-06dp']};

    white-space: nowrap;
    overflow: hidden;

    & > span {
        display: inline-block;
        opacity: ${(props) => (props.expanded ? 1 : 0)};
        width: ${(props) => (props.expanded ? `${props.textWidth}px` : 0)};
    }

    ${(props) =>
        props.expanded
            ? css`
                  & > span {
                      transition:
                          width 300ms,
                          opacity 300ms 300ms;
                  }

                  & > *:not(:last-child) {
                      transition: margin-right 300ms;
                  }
              `
            : css`
                  & > span {
                      transition:
                          width 300ms 300ms,
                          opacity 300ms;
                  }

                  & > *:not(:last-child) {
                      transition: margin-right 300ms 300ms;
                  }
              `}

    & > *:not(:last-child) {
        margin-right: ${(props) => props.expanded && `${props.theme.space.small}px`};
    }

    &:disabled {
        box-shadow: none;
        cursor: initial;
    }

    /** Fallback for browser without focus-visible */
    &:focus:not(:disabled):not([data-checked='true']):not(:active) {
        box-shadow: ${(props) => props.theme.shadows['shadow-06dp']}, ${(props) => props.theme.shadows['outline']};
    }

    &[data-checked='true']:focus:not(:disabled):not(:active) {
        box-shadow: ${(props) => props.theme.shadows['shadow-12dp']}, ${(props) => props.theme.shadows['outline']};
    }

    /** Remove mouse-focus for browser that support functionality */
    /** Auto-focus on click for unchecked button */
    &:focus:not(:focus-visible):not(:disabled):not([data-checked='true']):not(:active) {
        box-shadow: ${(props) => props.theme.shadows['shadow-06dp']};
    }

    /** Virtual-focus with keyboard for unchecked button */
    &:focus-visible:not(:disabled):not([data-checked='true']):not(:active) {
        box-shadow: ${(props) => props.theme.shadows['shadow-06dp']}, ${(props) => props.theme.shadows['outline']};
    }

    /** Auto-focus on click for checked button */
    &[data-checked='true']:focus:not(:focus-visible):not(:disabled):not(:active) {
        box-shadow: ${(props) => props.theme.shadows['shadow-12dp']};
    }

    /** Virtual-focus with keyboard for checked button */
    &[data-checked='true']:focus-visible:not(:disabled):not(:active) {
        box-shadow: ${(props) => props.theme.shadows['shadow-12dp']}, ${(props) => props.theme.shadows['outline']};
    }

    &:active:not(:disabled),
    &[data-checked='true']:not(:disabled):not(:focus) {
        box-shadow: ${(props) => props.theme.shadows['shadow-12dp']};
    }

    &::-moz-focus-inner,
    &::-moz-focus-outer {
        border: 0;
    }

    & > svg {
        width: 16px;
        height: 16px;
    }
`;

export const FabPrimary = styled(DefaultFab)`
    color: ${(props) => props.theme.colors['text-myzanichelli-blue-dark']};
    fill: ${(props) => props.theme.colors['text-myzanichelli-blue-dark']};
    background-color: ${(props) => props.theme.colors['accent-azure-main']};

    &:disabled {
        color: ${(props) => props.theme.colors['text-grey-700']};
        fill: ${(props) => props.theme.colors['text-grey-700']};
        background-color: ${(props) => props.theme.colors['surface-grey-200']};
    }

    &:active:not(:disabled) {
        background-color: ${(props) => props.theme.colors['accent-azure-light']};
    }

    &:hover:not(:disabled):not(:active) {
        background-color: ${(props) => props.theme.colors['accent-azure-dark']};
    }
`;

export const HelperVariant = styled(DefaultFab)`
    color: ${(props) => props.theme.colors['text-myzanichelli-blue-main']};
    fill: ${(props) => props.theme.colors['text-myzanichelli-blue-main']};
    background-color: ${(props) => props.theme.colors['surface-white']};

    &:disabled {
        color: ${(props) => props.theme.colors['text-grey-700']};
        fill: ${(props) => props.theme.colors['text-grey-700']};
        background-color: ${(props) => props.theme.colors['surface-grey-050']};
    }

    &:active:not(:disabled),
    &[data-checked='true']:not(:disabled) {
        color: ${(props) => props.theme.colors['text-myzanichelli-blue-dark']};
        fill: ${(props) => props.theme.colors['text-myzanichelli-blue-dark']};
        background-color: ${(props) => props.theme.colors['accent-azure-light']};
    }

    &:hover:not(:disabled):not(:active) {
        background-color: ${(props) => props.theme.colors['accent-azure-lighter']};
        color: ${(props) => props.theme.colors['text-myzanichelli-blue-dark']};
        fill: ${(props) => props.theme.colors['text-myzanichelli-blue-dark']};
    }
`;

/**
 * Create specific fab according on variant prop.
 * @param {string} type fab variant
 */
const createFab = (type) => {
    switch (type) {
        case 'helper':
            return HelperVariant;
        case 'primary':
        default:
            return FabPrimary;
    }
};

/**
 *
 * @param {*} props
 */
const Fab = ({
    children,
    disabled = false,
    onClick = () => {},
    text,
    variant = 'primary',
    checkable = false,
    resizableOnScroll = false,
}) => {
    const [checked, setChecked] = useState(false);
    const [expanded, setExpanded] = useState(true);
    const [currentPositionY, setCurrentPositionY] = useState(0);
    const [textWidth, setTextWidth] = useState();

    const ref = useCallback((node) => {
        if (node) {
            setTextWidth(node.getBoundingClientRect().width);
        }
    }, []);

    const { y: position } = useScrollPosition();

    useEffect(() => {
        if (!resizableOnScroll) {
            setExpanded(true);
        }
    }, [resizableOnScroll]);

    useEffect(() => {
        if (!resizableOnScroll) {
            return;
        }

        setCurrentPositionY(position);

        if (position > currentPositionY) {
            setExpanded(false);
        } else if (position < currentPositionY || position === 0) {
            setExpanded(true);
        }
    }, [position, currentPositionY, resizableOnScroll]);

    /**
     * Specific FAB to render according on variant prop.
     */
    const SpecificFab = createFab(variant);

    /**
     * Handler for click.
     * @param {event} e click event
     */
    const handleClick = (e) => {
        onClick(e, checkable && !checked);

        if (checkable) {
            setChecked((value) => !value);
        }
    };

    return (
        <SpecificFab
            disabled={disabled}
            onClick={handleClick}
            hasText={!!text}
            data-checked={checkable && checked}
            expanded={expanded || checked}
            textWidth={textWidth}
        >
            {children}
            {text && <span ref={ref}>{text}</span>}
        </SpecificFab>
    );
};

Fab.propTypes = {
    children: PropTypes.node,
    disabled: PropTypes.bool,
    onClick: PropTypes.func,
    text: PropTypes.string,
    variant: PropTypes.oneOf(['primary', 'helper']),
    checkable: PropTypes.bool,
    resizableOnScroll: PropTypes.bool,
};

export default Fab;
